import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import type {RoomConfig} from 'components/customer/RoomPlanner/helpers/measurement/types';
import {
    calculateCabinetWallDistances,
    getWallDistancePositionDelta,
} from 'components/customer/RoomPlanner/helpers/measurement/wallDistance';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';

export type HorizontalWall = 'left' | 'right' | 'front' | 'back';

// Upper bound for a typed distance; far beyond any room, it only stops
// absurd input before it reaches the placement checks.
export const MAX_WALL_DISTANCE_MM = 100000;

// Whole millimetres with at most one decimal place, as the labels show.
const DISTANCE_INPUT_PATTERN = /^\d*\.?\d?$/;

/**
 * Whether a partially typed value may stay in the distance input: digits
 * with at most one decimal place.
 * @param {string} value - The input's new value
 * @return {boolean} True if the value can be typed
 */
export const isAllowedDistanceInput = (value: string): boolean =>
    DISTANCE_INPUT_PATTERN.test(value);

/**
 * Parses a typed wall distance.
 * @param {string} value - The input's value
 * @return {{mm: number} | {error: string}} The distance in mm, or why it
 * can't be used
 */
export const parseWallDistanceInput = (
    value: string
): {mm: number} | {error: string} => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '.') {
        return {error: 'Enter a distance'};
    }
    if (!DISTANCE_INPUT_PATTERN.test(trimmed)) {
        return {error: 'Numbers only, up to 1 decimal'};
    }

    const mm = parseFloat(trimmed);
    if (!Number.isFinite(mm)) {
        return {error: 'Enter a distance'};
    }
    if (mm > MAX_WALL_DISTANCE_MM) {
        return {error: `Max ${MAX_WALL_DISTANCE_MM} mm`};
    }

    return {mm};
};

/**
 * Where the cabinet must move so its gap to `wall` becomes `targetMm`,
 * measured the same way as the wall-distance labels.
 * @param {CabinetMesh} cabinet - The cabinet to move
 * @param {HorizontalWall} wall - The wall the distance is measured to
 * @param {number} targetMm - The wanted distance in mm
 * @param {RoomConfig} roomConfig - Room dimensions and scale
 * @return {THREE.Vector3} The cabinet's new position
 */
export const getWallDistanceTargetPosition = (
    cabinet: CabinetMesh,
    wall: HorizontalWall,
    targetMm: number,
    roomConfig: RoomConfig
): THREE.Vector3 => {
    const distances = calculateCabinetWallDistances(cabinet, roomConfig);
    const currentScaled =
        wall === 'left'
            ? Math.abs(distances.left)
            : wall === 'right'
              ? Math.abs(distances.right)
              : wall === 'front'
                ? Math.abs(distances.front)
                : Math.abs(distances.back);

    const currentDirection = cabinet.userData.currentDirection || 'front';
    const {x, z} = getWallDistancePositionDelta(
        currentDirection,
        wall,
        targetMm * roomConfig.scale - currentScaled
    );

    return cabinet.position.clone().add(new THREE.Vector3(x, 0, z));
};

// How far (in mm) the wall clamp may stop the cabinet short of a typed
// distance and still count as reaching the wall. The distance labels and
// the wall clamp use different wall offsets (18 mm apart for a front-facing
// cabinet's front wall in an L-shaped room), so a typed 0 lands past where
// dragging stops the cabinet flush against the wall.
const WALL_CLAMP_TOLERANCE_MM = 50;

/**
 * Where the cabinet ends up when its gap to `wall` is set to `targetMm`,
 * using the same wall and overlap checks as the move editor's preview.
 * A target just past the wall (e.g. 0 mm) snaps flush against it.
 * @param {CabinetMesh} cabinet - The cabinet being moved
 * @param {HorizontalWall} wall - The wall the distance is measured to
 * @param {number} targetMm - The wanted distance in mm
 * @param {CabinetMesh[]} cabinets - All cabinets in the room
 * @param {RoomConfig} roomConfig - Room type, dimensions and scale
 * @return {{position: THREE.Vector3} | {error: string}} The new position,
 * or why the cabinet can't go there
 */
export const resolveWallDistancePlacement = (
    cabinet: CabinetMesh,
    wall: HorizontalWall,
    targetMm: number,
    cabinets: CabinetMesh[],
    roomConfig: RoomConfig
): {position: THREE.Vector3} | {error: string} => {
    let position = getWallDistanceTargetPosition(
        cabinet,
        wall,
        targetMm,
        roomConfig
    );

    const scene = cabinet.parent;
    const wallMesh = scene?.children.find(
        (child) =>
            child.name === ObjectNameMap.rectangularWall ||
            child.name === ObjectNameMap.lShapeWall
    ) as
        | THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>
        | undefined;
    if (wallMesh) {
        const clampAt = (target: THREE.Vector3) =>
            productClamping(
                cabinet,
                wallMesh,
                CameraSetup.camera,
                new THREE.Vector2(0, 0),
                target,
                roomConfig.roomType,
                false,
                true // preventAutoRotation
            );
        const result = clampAt(position);
        if (result?.wasClamped) {
            const currentDirection =
                cabinet.userData.currentDirection || 'front';
            const movesOnX =
                getWallDistancePositionDelta(currentDirection, wall, 1).x !== 0;
            // A position's coordinate along the move, or across it.
            const along = (v: THREE.Vector3) => (movesOnX ? v.x : v.z);
            const across = (v: THREE.Vector3) => (movesOnX ? v.z : v.x);

            // A cabinet can already sit a little outside the clamp's bounds
            // (e.g. just placed 2 mm off a wall the clamp keeps further
            // away), which clamps every target across the move. Only a
            // cross shift this move introduces means it doesn't fit.
            const current = clampAt(cabinet.position.clone());
            const crossShift = Math.abs(
                across(result.clamped) -
                    (current ? across(current.clamped) : across(position))
            );
            if (crossShift > 1e-3) {
                return {error: "Doesn't fit, it would go past the wall"};
            }

            const start = along(cabinet.position);
            const target = along(position);
            const stop = along(result.clamped);

            if (Math.abs(target - stop) > 1e-3) {
                // Accept the clamp only if the cabinet was heading towards
                // `wall` and got stopped a little short of the typed
                // distance, i.e. flush against that wall. Anything else
                // really doesn't fit.
                const distances = calculateCabinetWallDistances(
                    cabinet,
                    roomConfig
                );
                const currentMm =
                    Math.abs(
                        wall === 'left'
                            ? distances.left
                            : wall === 'right'
                              ? distances.right
                              : wall === 'front'
                                ? distances.front
                                : distances.back
                    ) / roomConfig.scale;

                const stopsShortOfTarget =
                    Math.min(start, target) <= stop &&
                    stop <= Math.max(start, target);
                const shortfallMm = Math.abs(target - stop) / roomConfig.scale;

                if (
                    targetMm >= currentMm ||
                    !stopsShortOfTarget ||
                    shortfallMm > WALL_CLAMP_TOLERANCE_MM
                ) {
                    return {error: "Doesn't fit, it would go past the wall"};
                }
                position = position.clone();
                if (movesOnX) position.x = stop;
                else position.z = stop;
            }
        }
    }

    // Same small escape buffer as keyboard/move-editor movement, so a
    // cabinet snapped against a neighbour can still slide away from it.
    const escapeBuffer = -2;
    const overlapping = checkCabinetOverlap(
        cabinet,
        position,
        cabinets,
        escapeBuffer
    );
    const bothFloorProducts =
        overlapping?.some(
            (other) => other.userData.isFloorProduct || other.userData.noBottom
        ) && cabinet.userData.isFloorProduct;
    if (overlapping && (bothFloorProducts || !cabinet.userData.ignoreOverlap)) {
        return {error: 'Overlaps another cabinet'};
    }

    return {position};
};

/**
 * Re-syncs the movement arrows and rebuilds the wall-distance labels after
 * the cabinet was moved programmatically.
 * @param {CabinetMesh} cabinet - The cabinet that moved
 */
export const refreshMovementOverlays = (cabinet: CabinetMesh) => {
    const scene = cabinet.parent;
    if (!scene) return;

    const {width, depth, height} = getGroupWidth(cabinet);

    scene.children.forEach((child) => {
        if (child.name !== 'cabinetMovementArrows') return;

        child.position.copy(cabinet.position);

        const onDrag = (
            child.userData as {
                onDrag?: (width: number, depth: number, height: number) => void;
            }
        ).onDrag;
        if (typeof onDrag === 'function') {
            onDrag(width, depth, height);
        }
    });
};
